import { useRouter } from 'expo-router';
import { useAuthUrl } from 'features/auth/hooks/useAuthUrl/useAuthUrl';
import fetchSelectChallenge from 'features/auth/resetPassword/utils/fetchSelectChallenge/fetchSelectChallenge';
import {
  MagicLinkErrorCodes,
  VerifyIdentityErrorScreenEnum,
} from 'features/otp/challenges/VerifyIdentity/VerifyIdentityError/VerifyIdentityErrorScreenEnum';
import { useContext, useState } from 'react';
import { FeatureFlagKey } from 'services/statsig/types';
import { getLambdaHeaders } from 'shared/hooks/auth/useLambdaHeaders';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

export interface ResendMagicLinkData {
  challengeId: string;
  identityId: string;
}

export function useResendMagicLink() {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const { getVaroSessionId, idToken } =
    useContext<AuthContextProps>(AuthContext);
  const authUrl = useAuthUrl(
    FeatureFlagKey.SELECT_CHALLENGE_USE_K8S_AUTH_SERVICE,
  );

  const resendMagicLink = async (data: ResendMagicLinkData) => {
    setLoading(true);

    const varoSessionId = await getVaroSessionId();
    const headers = await getLambdaHeaders(idToken, varoSessionId);

    try {
      const { errorCode, message, title, challengeState, challengeType } =
        await fetchSelectChallenge(
          authUrl,
          {
            ...data,
          },
          headers,
        );

      setLoading(false);

      if (errorCode) {
        router.replace({
          pathname: '/login/verify-identity-error',
          params: {
            screenType: getErrorScreen(errorCode),
          },
        });
      }

      return { challengeState, challengeType, errorCode, message, title };
    } catch (error) {
      setLoading(false);

      logErrorWithContext('ResendMagicLink error: ' + error, {
        featureArea: FeatureArea.AUTHENTICATION,
        responsibleTeam: ResponsibleTeam.FRAUD,
        severity: ErrorSeverity.CRITICAL,
      });

      router.replace({
        pathname: '/login/verify-identity-error',
        params: {
          screenType: VerifyIdentityErrorScreenEnum.Error,
        },
      });
    }
  };

  return {
    resendMagicLink,
    loading,
  };
}

const getErrorScreen = (errorCode: MagicLinkErrorCodes) => {
  switch (errorCode) {
    case MagicLinkErrorCodes.LinkExpired:
      return VerifyIdentityErrorScreenEnum.LinkExpired;
    case MagicLinkErrorCodes.TooManyRequests:
      return VerifyIdentityErrorScreenEnum.TooManyRequests;
    default:
      return VerifyIdentityErrorScreenEnum.Error;
  }
};
